import { http } from './http'

export interface IdpUser {
  userId: string
  id?: string
  /** 登录账号（交大 account） */
  account: string
  /** 学/工号（交大 code） */
  code?: string
  name?: string
  userName?: string
  userType?: string
  roleCode?: string
  roleName?: string
  entry?: string
  menus?: { menuId: string; title: string; path?: string; icon?: string }[]
  enabled?: boolean
  mobile?: string
  schoolCode?: string
  schoolName?: string
  deptCode?: string
  deptName?: string
  positionCode?: string
  positionName?: string
  gender?: string
  idCard?: string
  stageCode?: string
  gradeCode?: string
  stageName?: string
  gradeName?: string
}

export function login(
  username: string,
  password: string,
  opts?: {
    loginType?: string
    schoolCode?: string
    captchaId?: string
    captchaAnswer?: string
  }
) {
  return http
    .post<{ data: IdpUser }>('/idp/login', {
      username,
      password,
      loginType: opts?.loginType,
      schoolCode: opts?.schoolCode,
      captchaId: opts?.captchaId,
      captchaAnswer: opts?.captchaAnswer,
    })
    .then((r) => r.data.data)
}

export function fetchCaptcha() {
  return http
    .get<{ data: { captchaId: string; question: string } }>('/idp/captcha')
    .then((r) => r.data.data)
}

export function changePassword(oldPassword: string, newPassword: string) {
  return http.post('/idp/password/change', { oldPassword, newPassword }).then((r) => r.data.data)
}

export function resetUserPassword(userId: string, password?: string) {
  return http
    .post<{ data: { account: string; password: string } }>(
      `/idp/users/${encodeURIComponent(userId)}/reset-password`,
      { password }
    )
    .then((r) => r.data.data)
}

export function kickUser(userId: string) {
  return http.post(`/idp/users/${encodeURIComponent(userId)}/kick`).then((r) => r.data.data)
}

export function exportUsers(params?: { keyword?: string; schoolCode?: string }) {
  return http
    .get<{ data: IdpUser[] }>('/idp/users/export', { params })
    .then((r) => r.data.data)
}

export function importUsers(rows: Record<string, unknown>[]) {
  return http
    .post<{ data: { ok: number; fail: number; errors: { row: number; msg: string }[] } }>(
      '/idp/users/import',
      { rows }
    )
    .then((r) => r.data.data)
}

export function fetchMetaSchools() {
  return http
    .get<{ data: { schools: Record<string, unknown>[] } }>('/idp/meta/schools')
    .then((r) => r.data.data)
}

export function fetchMetaStages() {
  return http
    .get<{
      data: {
        stages: {
          stageCode: string
          stageName: string
          grades: { gradeCode: string; gradeName: string }[]
        }[]
      }
    }>('/idp/meta/stages')
    .then((r) => r.data.data)
}

export function logout() {
  return http.post('/idp/logout').then((r) => r.data)
}

export function fetchMe() {
  return http.get<{ data: IdpUser }>('/idp/me').then((r) => r.data.data)
}

export function fetchApps() {
  return http
    .get<{
      data: {
        clientId: string
        name: string
        ssoStartUrl: string
        ready: boolean
      }[]
    }>('/idp/apps')
    .then((r) => r.data.data)
}

export function listRoles() {
  return http.get<{ data: Record<string, unknown>[] }>('/idp/roles').then((r) => r.data.data)
}

export function createRole(body: Record<string, unknown>) {
  return http.post('/idp/roles', body).then((r) => r.data.data)
}

export function updateRole(code: string, body: Record<string, unknown>) {
  return http.put(`/idp/roles/${encodeURIComponent(code)}`, body).then((r) => r.data.data)
}

export function deleteRole(code: string) {
  return http.delete(`/idp/roles/${encodeURIComponent(code)}`).then((r) => r.data)
}

export function listMenus() {
  return http.get<{ data: Record<string, unknown>[] }>('/idp/menus').then((r) => r.data.data)
}

export function listUsers(params?: {
  keyword?: string
  schoolCode?: string
  deptCode?: string
  current?: number
  size?: number
}) {
  return http.get<{ data: { records: IdpUser[]; total: number } }>('/idp/users', { params }).then((r) => r.data.data)
}

export function getUser(id: string) {
  return http.get<{ data: IdpUser }>(`/idp/users/${encodeURIComponent(id)}`).then((r) => r.data.data)
}

export function createUser(body: Record<string, unknown>) {
  return http.post('/idp/users', body).then((r) => r.data.data)
}

export function updateUser(id: string, body: Record<string, unknown>) {
  return http.put(`/idp/users/${encodeURIComponent(id)}`, body).then((r) => r.data.data)
}

export function deleteUser(id: string) {
  return http.delete(`/idp/users/${encodeURIComponent(id)}`).then((r) => r.data)
}

export function listClients() {
  return http.get<{ data: Record<string, unknown>[] }>('/oauth2/clients').then((r) => r.data.data)
}

export function getClient(id: string) {
  return http.get<{ data: Record<string, unknown> }>(`/oauth2/clients/${encodeURIComponent(id)}`).then((r) => r.data.data)
}

export function upsertClient(body: Record<string, unknown>) {
  return http.post('/oauth2/clients', body).then((r) => r.data.data)
}

export function setClientEnabled(id: string, enabled: boolean) {
  return http.post(`/oauth2/clients/${encodeURIComponent(id)}/enabled`, { enabled }).then((r) => r.data.data)
}

export function rotateSecret(id: string) {
  return http.post(`/oauth2/clients/${encodeURIComponent(id)}/rotate-secret`).then((r) => r.data.data)
}

export function deleteClient(id: string) {
  return http.delete(`/oauth2/clients/${encodeURIComponent(id)}`).then((r) => r.data)
}

export function listAudit(params?: {
  keyword?: string
  center?: 'user' | 'auth'
  scope?: 'idp' | 'oauth' | 'all'
  current?: number
  size?: number
}) {
  return http
    .get<{ data: { records: unknown[]; total: number } }>('/idp/op-logs', { params })
    .then((r) => r.data.data)
}

export function fetchOverviewStats(params?: { days?: number }) {
  return http
    .get<{ data: Record<string, unknown> }>('/idp/stats/overview', { params })
    .then((r) => r.data.data)
}

export function startAppSso(clientId: string) {
  return http
    .post<{ data: { redirectUrl: string; clientId: string; ssoStartUrl: string } }>(
      '/idp/sso-entry',
      { clientId }
    )
    .then((r) => r.data.data)
}

export function listStagesAdmin() {
  return http.get<{ data: Record<string, unknown>[] }>('/idp/stages').then((r) => r.data.data)
}
export function upsertStage(body: Record<string, unknown>) {
  return http.post('/idp/stages', body).then((r) => r.data.data)
}
export function deleteStage(code: string) {
  return http.delete(`/idp/stages/${encodeURIComponent(code)}`).then((r) => r.data)
}
export function upsertGrade(body: Record<string, unknown>) {
  return http.post('/idp/grades', body).then((r) => r.data.data)
}
export function deleteGrade(stageCode: string, gradeCode: string) {
  return http
    .delete(
      `/idp/grades/${encodeURIComponent(stageCode)}/${encodeURIComponent(gradeCode)}`
    )
    .then((r) => r.data)
}

export function httpGetOrgTree(params?: { groupCode?: string; schoolCode?: string }) {
  return http.get<{ data: unknown[] }>('/idp/org-tree', { params }).then((r) => r.data.data)
}

export function listGroups() {
  return http.get<{ data: Record<string, unknown>[] }>('/idp/groups').then((r) => r.data.data)
}
export function upsertGroup(body: Record<string, unknown>) {
  return http.post('/idp/groups', body).then((r) => r.data.data)
}
export function deleteGroup(code: string) {
  return http.delete(`/idp/groups/${encodeURIComponent(code)}`).then((r) => r.data)
}

export function listSchools(params?: { groupCode?: string }) {
  return http
    .get<{ data: Record<string, unknown>[] }>('/idp/schools', { params })
    .then((r) => r.data.data)
}
export function upsertSchool(body: Record<string, unknown>) {
  return http.post('/idp/schools', body).then((r) => r.data.data)
}
export function deleteSchool(code: string) {
  return http.delete(`/idp/schools/${encodeURIComponent(code)}`).then((r) => r.data)
}

export function listDepts(params?: { schoolCode?: string }) {
  return http.get<{ data: Record<string, unknown>[] }>('/idp/depts', { params }).then((r) => r.data.data)
}
export function upsertDept(body: Record<string, unknown>) {
  return http.post('/idp/depts', body).then((r) => r.data.data)
}
export function deleteDept(code: string) {
  return http.delete(`/idp/depts/${encodeURIComponent(code)}`).then((r) => r.data)
}

export function listPositions() {
  return http.get<{ data: Record<string, unknown>[] }>('/idp/positions').then((r) => r.data.data)
}
export function upsertPosition(body: Record<string, unknown>) {
  return http.post('/idp/positions', body).then((r) => r.data.data)
}
export function deletePosition(code: string) {
  return http.delete(`/idp/positions/${encodeURIComponent(code)}`).then((r) => r.data)
}
